import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import {
  Briefcase,
  CheckCircle2,
  Clock,
  Star,
  Target,
  TrendingUp,
  Users,
  ArrowRight,
} from 'lucide-react';

/**
 * StandupCard — compact per-recruiter card optimised for morning standup.
 * Shows what they did TODAY (subs / closures), where they are RIGHT NOW
 * (active pipeline / open jobs), and how close they are to MONTHLY goal.
 *
 * Designed to fit ~3 cards per row on desktop so a manager can scan their
 * pinned recruiters at a glance.
 */
const StandupCard = ({ r, onOpen }) => {
  const goalDone = r.monthly_closures_done || 0;
  const goal = r.monthly_goal || 0;
  const goalPct = goal ? Math.min(100, Math.round((goalDone / goal) * 100)) : 0;
  const goalVariant = goalDone >= goal && goal > 0
    ? 'achieved'
    : goalPct >= 60
    ? 'ontrack'
    : goalDone === 0
    ? 'cold'
    : 'behind';

  const goalColor = {
    achieved: 'bg-emerald-500',
    ontrack: 'bg-teal-500',
    cold: 'bg-amber-400',
    behind: 'bg-rose-500',
  }[goalVariant];

  const goalLabel = {
    achieved: 'Goal hit',
    ontrack: 'On track',
    cold: 'No closures yet',
    behind: 'Behind',
  }[goalVariant];

  const subsToday = r.submissions_today || 0;
  const closuresToday = r.closures_today || 0;
  const hadActivityToday = subsToday > 0 || closuresToday > 0;

  return (
    <Card
      className="relative p-4 hover:shadow-md transition cursor-pointer border border-amber-100/70 hover:border-amber-300"
      onClick={onOpen}
      data-testid={`standup-card-${r.user_id}`}
    >
      {/* Pinned indicator */}
      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400 absolute top-3 right-3" />

      {/* Recruiter header */}
      <div className="flex items-center gap-3 mb-3 pr-6">
        <span className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-700 flex-shrink-0">
          {r.initials || (r.name || '??').slice(0, 2).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 truncate">{r.name}</div>
          <div className="text-[10px] text-slate-500 truncate">{r.email}</div>
        </div>
      </div>

      {/* Today's activity — the headline of the standup */}
      <div
        className={`rounded-md px-3 py-2 mb-3 ${
          hadActivityToday ? 'bg-emerald-50 border border-emerald-100' : 'bg-slate-50 border border-slate-100'
        }`}
      >
        <div className="text-[9px] uppercase tracking-wide text-slate-500 font-bold mb-1.5">
          Today's activity
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-1.5">
            <TrendingUp
              className={`w-3.5 h-3.5 flex-shrink-0 ${
                subsToday > 0 ? 'text-emerald-600' : 'text-slate-400'
              }`}
            />
            <div className="min-w-0">
              <div className={`text-base font-bold leading-tight ${subsToday > 0 ? 'text-emerald-700' : 'text-slate-500'}`}>
                {subsToday}
              </div>
              <div className="text-[10px] text-slate-500 leading-tight">
                Submissions
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <CheckCircle2
              className={`w-3.5 h-3.5 flex-shrink-0 ${
                closuresToday > 0 ? 'text-emerald-600' : 'text-slate-400'
              }`}
            />
            <div className="min-w-0">
              <div className={`text-base font-bold leading-tight ${closuresToday > 0 ? 'text-emerald-700' : 'text-slate-500'}`}>
                {closuresToday}
              </div>
              <div className="text-[10px] text-slate-500 leading-tight">
                Closures
              </div>
            </div>
          </div>
        </div>
        {!hadActivityToday ? (
          <div className="text-[10px] text-slate-400 italic mt-1.5">No activity logged today yet.</div>
        ) : null}
      </div>

      {/* Pipeline + jobs row */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-md border border-slate-100 px-2.5 py-1.5">
          <div className="flex items-center gap-1 text-[9px] uppercase tracking-wide text-slate-500 font-bold mb-0.5">
            <Users className="w-3 h-3" /> Active pipeline
          </div>
          <div className="text-sm font-bold text-slate-800">{r.active_candidates || 0}</div>
        </div>
        <div className="rounded-md border border-slate-100 px-2.5 py-1.5">
          <div className="flex items-center gap-1 text-[9px] uppercase tracking-wide text-slate-500 font-bold mb-0.5">
            <Briefcase className="w-3 h-3" /> Open jobs
          </div>
          <div className="text-sm font-bold text-slate-800">{r.jobs_assigned || 0}</div>
        </div>
      </div>

      {/* Goal progress bar */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="flex items-center gap-1 text-[9px] uppercase tracking-wide text-slate-500 font-bold">
            <Target className="w-3 h-3" /> Monthly goal
          </span>
          <span className="text-[10px] font-mono font-semibold text-slate-700">
            {goalDone}/{goal || '—'}
          </span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-1.5 transition-all ${goalColor}`}
            style={{ width: `${goalPct}%` }}
          />
        </div>
        <div className="text-[10px] text-slate-500 mt-1">{goalLabel}</div>
      </div>

      {/* CTA */}
      <Button
        variant="ghost"
        size="sm"
        className="mt-3 w-full text-[11px] h-7 text-slate-600 hover:text-slate-900 hover:bg-slate-50"
        onClick={(e) => {
          e.stopPropagation();
          onOpen?.();
        }}
        data-testid={`standup-open-${r.user_id}`}
      >
        Open dashboard <ArrowRight className="w-3 h-3 ml-1" />
      </Button>
    </Card>
  );
};

export default StandupCard;
